<template>
    <form v-on:submit.prevent="submit">
        <template v-for="field in state.fields">
            <div class="form-group row">
                <div class="col-2 text-truncate">
                    <label :title="field.name" class="col-form-label">
                        {{ field.name }}
                    </label>
                </div>
                <div class="col-1 d-flex align-items-center">
                    <span v-if="field.required" class="text-danger">*</span>

                    <CustomFieldPopover
                        v-if="field.description"
                        :label="field.name"
                        :field="field"
                    >
                    </CustomFieldPopover>

                </div>
                <div class="input-group col-9">
                    <div v-if="field.updateRule" class="input-group-prepend">
                        <div class="input-group-text">
                            {{ field.updateRule }}
                        </div>
                    </div>
                    <div v-if="field.loading" class="loader"></div>
                    <industry-selector
                        v-else-if="field.validation === 'industry'"
                        :recordId="state.recordId"
                        :required="field.required"
                        :initialVal="field.value"
                        @input="setFieldValue(field.key, $event)"
                        :industryType="field.industry_type || 'linkedin'" />
                    <text-input
                        v-else-if="field.type === 'text'"
                        :name="field.key"
                        :value="field.value"
                        :validation="field.validation"
                        :required="field.required"
                        :error="field.error"
                        :validChars="field.validChars"
                        @input="setFieldValue(field.key, $event)"
                    ></text-input>
                    <select-input
                        v-else-if="field.type === 'select'"
                        :name="field.key"
                        :choices="field.choices"
                        :value="field.value"
                        :error="field.error"
                        :required="field.required"
                        :prefixed="!!field.updateRule"
                        :postpended="!!field.postpend"
                        @input="setFieldValue(field.key, $event)"
                    ></select-input>
                    <template v-else-if="field.type === 'multi-picklist'">
                        <multi-picklist-input
                            v-if="field.key !== 'industry' && field.choices != null"
                            :name="field.key"
                            :choices="field.choices"
                            :value="field.value"
                            :required="field.required"
                            :prefixed="!!field.updateRule"
                            :postpended="!!field.postpend"
                            :virtual="field.choices.length >= 100"
                            @input="setFieldValue(field.key, $event)"
                        ></multi-picklist-input>
                     </template>
                    <template v-else-if="field.type === 'multi-select'">
                        <multi-select-input
                            v-if="field.key !== 'industry' && field.choices != null"
                            :name="field.key"
                            :choices="field.choices"
                            :value="field.value"
                            :required="field.required"
                            :prefixed="!!field.updateRule"
                            :postpended="!!field.postpend"
                            :virtual="field.choices.length >= 100"
                            @input="setFieldValue(field.key, $event)"
                        ></multi-select-input>
                        <industry-selector v-else
                            :recordId="state.recordId"
                            :required="field.required"
                            :initialVal="field.value"
                            @input="setFieldValue(field.key, $event)"
                            :industryType="field.industry_type || 'linkedin'" />
                    </template>
                    <postpend
                        v-if="!field.postpend.disabled"
                        :type="field.postpend.type"
                        :value="field.postpend.value"
                        :id="field.key"
                        :tooltip="field.postpend.tooltip"
                        :postpendClass="field.postpend.class"
                    ></postpend>
                    <div v-if="field.error" class="invalid-feedback">
                        {{ field.error }}
                    </div>
                </div>
            </div>
            <!-- Field verification -->
            <div v-if="field.verification" class="form-group row">
                <!-- Empty label for verification link input -->
                <label class="col-3 col-form-label text-truncate"></label>
                <verification
                    :name="field.key"
                    :value="field.verificationValue"
                    :error="field.verificationError"
                    @input="setFieldVerificationValue(field.key, $event)"
                ></verification>
            </div>
        </template>
        <b-modal ref="fieldDescriptionModal" size="lg" body-class="px-3 pt-3 pb-3" modal-class="mt-5"
                 :hide-footer="true">
            <div slot="modal-title">
                <h4 class="m-0 pl-1">{{state.currentField.name}}</h4>
            </div>
            <section>
                <vue-editor class="disabled" :disabled="true" :value="state.currentField.description"
                            :editorToolbar="[]"></vue-editor>
            </section>
        </b-modal>
    </form>
</template>

<script>
    import getters from '../../../utils/getters';
    import CONSTANTS from '../../../constants';
    import {VueEditor} from 'vue2-editor';

    import TextInput from './_text-input.vue';
    import SelectInput from './_select-input.vue';
    import MultiSelectInput from './_multi-select-input.vue';
    import MultiPicklistInput from './_multi-picklist-input.vue';
    import Verification from './_verification.vue';
    import Postpend from './_postpend.vue';
    import IndustrySelector from './industry-selector';
    import CustomFieldPopover from './custom-field-popover.vue'

    const EVENTS = {
        FIELD_INPUT: 'fieldInput',
        FIELD_VERIFICATION_INPUT: 'fieldVerificationInput',
    };

    export default {
        name: 'record-form',
        props: {
            storePath: {type: String, required: true},
        },
        components: {
            'text-input': TextInput,
            'select-input': SelectInput,
            'multi-select-input': MultiSelectInput,
            'multi-picklist-input': MultiPicklistInput,
            'verification': Verification,
            'postpend': Postpend,
            VueEditor,
            IndustrySelector,
            CustomFieldPopover
        },
        computed:  {
            state,
        },
        methods: {
            setFieldValue,
            setFieldVerificationValue,
        },
        beforeDestroy() {
            this.$store.commit(`${this.storePath}/clearValues`);
        }
    };

    function state() {
        return getters.getValueByPropertyName(this.$store.state, this.storePath, '/');
    }

    function setFieldValue(key, value) {
        this.$store.dispatch(`${this.storePath}/processValue`, [key, value]).then(value => {
            this.$store.commit(`${this.storePath}/setFieldAttr`, [key, 'value', value]);
            this.$emit(EVENTS.FIELD_INPUT, key, value);
            this.$store.dispatch(`${this.storePath}/toggleFieldPostpend`, [key, value]);
            this.$store.dispatch(`${this.storePath}/validateField`, [key, value]);
        });
    }

    function setFieldVerificationValue(key, value) {
        this.$store.commit(`${this.storePath}/setFieldAttr`, [key, 'verificationValue', value]);
        this.$emit(EVENTS.FIELD_VERIFICATION_INPUT, [key, value]);
        this.$store.dispatch(`${this.storePath}/validateVerificationField`, [key, value]);
    }
</script>
